import {defineStore} from 'pinia';
import {
  SavedOptions,
  Options,
  setNewOptions,
} from '@core/helpers/persistOptionsHelper';
import {AxiosResponse, isAxiosError} from 'axios';
import {
  setLimitAndSkipFilterOptions,
  setNameSearchFilterOptionsLogging,
} from '@core/helpers/servicesHelper';
import {Team} from '@internalDevportal/store/TeamStore';
import {Spec} from '@internalDevportal/store/SpecStore';
import {ref} from 'vue';
import ApiService from '@core/services/ApiService';

export interface PermissionGroup {
  id: string;
  specId?: string | null;
  name: string;
  description: string;
  specs?: Spec[] | null;
  teams?: Team[];
}

interface NewPermissionGroup {
  name: string;
  description: string;
}

interface EditPermissionGroup {
  id: string;
  name: string;
  description: string;
}

export interface EmailTemplate {
  id?: number;
  name: string;
  subject: string;
  text: string;
  html: string;
  fromEmail: string;
  fromName: string;
  isEditable: boolean;
}

export const useAdminStore = defineStore(
  'UseAdminStore',
  () => {
    const apiService = ApiService;

    const roles = ref<string[]>([]);
    const emailTemplates = ref<Array<EmailTemplate>>([]);
    const permissionGroups = ref<Array<PermissionGroup>>([]);
    const loggingOptions = ref<SavedOptions>({
      itemsPerPage: 10,
      page: 1,
      sortBy: [{key: 'date', order: 'desc'}],
      filter: {nameSearch: undefined},
    });
    const loggingNameSearch = ref<string>('');
    const emailTemplateDetails = ref<EmailTemplate>({} as EmailTemplate);
    const permissionGroupDetails = ref<PermissionGroup>();
    const logs = ref<Record<string, unknown>[]>([]);
    const loggingCount = ref<number>(0);
    const optionsEmail = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });
    const newOptionsPermissionGroups = ref<SavedOptions>({
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      groupBy: [],
      multiSort: false,
      mustSort: false,
      filter: {},
    });

    function getPermissionGroupById(id: string) {
      const fromList = permissionGroups.value.find(
        (permissionGroup: {id: string}) => permissionGroup.id === id,
      );
      if (fromList) {
        return fromList;
      }
      if (permissionGroupDetails.value?.id === id) {
        return permissionGroupDetails.value;
      }
      return undefined;
    }

    function rolesPath(environmentId?: string) {
      return environmentId
        ? `/roles?environmentId=${encodeURIComponent(environmentId)}`
        : '/roles';
    }

    async function getRoles(environmentId?: string) {
      try {
        const response = await apiService
          .getDefaultApiInstance()
          .get(rolesPath(environmentId));
        roles.value = response.data.roles;
        return response.data.roles;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          console.warn(
            'GET /roles returned 404; roles unavailable. Is the API running and exposing /roles (same base as /modules)?',
          );
          roles.value = [];
          return [];
        }
        throw error;
      }
    }

    async function onlyGetRoles(environmentId?: string): Promise<string[]> {
      try {
        const response = await apiService
          .getDefaultApiInstance()
          .get(rolesPath(environmentId));
        return response.data.roles;
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          console.warn(
            'GET /roles returned 404; roles unavailable. Is the API running and exposing /roles (same base as /modules)?',
          );
          return [];
        }
        throw error;
      }
    }

    async function saveLoggingOptions(options: Options) {
      loggingOptions.value = setNewOptions(options);
      loggingOptions.value.filter = {nameSearch: loggingNameSearch.value};
      await Promise.all([
        getLogging(loggingOptions.value),
        getLoggingCount(loggingOptions.value),
      ]);
    }

    async function saveLoggingSearch(value: string) {
      if (loggingOptions.value.filter) {
        loggingOptions.value.filter.nameSearch = value;
      }
      loggingNameSearch.value = value;
      loggingOptions.value.page = 1;
      await Promise.all([
        getLogging(loggingOptions.value),
        getLoggingCount(loggingOptions.value),
      ]);
    }

    async function getLogging(options: Options) {
      let filterObject = setLimitAndSkipFilterOptions({}, options);
      filterObject = setNameSearchFilterOptionsLogging(filterObject, options);
      const queryParams = `?filter=${encodeURI(JSON.stringify(filterObject))}`;
      const response = await apiService
        .getApiInstance()
        .get(`/logs${queryParams === '?' ? '' : queryParams}`);
      logs.value = response.data;
    }

    async function getLoggingCount(options: Options) {
      const filterObject = setNameSearchFilterOptionsLogging({}, options);
      const queryParams = `?where=${encodeURI(
        JSON.stringify(filterObject.where),
      )}`;
      const response = await apiService
        .getApiInstance()
        .get(
          `/logs/count${queryParams === '?where=undefined' ? '' : queryParams}`,
        );
      loggingCount.value = response.data.count;
    }

    async function loadEmailTemplates() {
      const response = await apiService.getApiInstance().get('/emailtemplates');
      emailTemplates.value = response.data;
    }

    async function getEmailTemplateDetails(id: number) {
      const response = await apiService
        .getApiInstance()
        .get(`/emailtemplates/${id}`);
      emailTemplateDetails.value = response.data;
    }

    async function editEmailTemplate(emailTemplateUpdates: EmailTemplate) {
      const id = emailTemplateDetails.value.id!;
      await apiService
        .getApiInstance()
        .put(`/emailtemplates/${id}`, emailTemplateUpdates);
    }

    async function loadPermissionGroups() {
      const response = await apiService
        .getApiInstance()
        .get('/permissiongroups?filter={"include":["teams", "specs"]}');
      permissionGroups.value = response.data;
    }

    async function loadPermissionGroupById(id: string) {
      const response = await apiService
        .getApiInstance()
        .get(`/permissiongroups/${id}?filter={"include":["teams", "specs"]}`);
      permissionGroupDetails.value = response.data;
    }

    async function fetchPermissionGroupById(
      id: string,
    ): Promise<PermissionGroup> {
      const response = await apiService
        .getApiInstance()
        .get(`/permissiongroups/${id}?filter={"include":["teams", "specs"]}`);
      return response.data;
    }

    async function createPermissionGroup(
      payload: NewPermissionGroup,
    ): Promise<AxiosResponse> {
      const response = await apiService
        .getApiInstance()
        .post('/permissiongroups', payload);
      await loadPermissionGroups();
      return response;
    }

    async function editPermissionGroup(
      permissionGroupUpdates: EditPermissionGroup,
    ): Promise<AxiosResponse> {
      const {id} = permissionGroupUpdates;
      const response = await apiService
        .getApiInstance()
        .put(`/permissiongroups/${id}`, permissionGroupUpdates);
      await loadPermissionGroups();
      return response;
    }

    async function deletePermissionGroup(
      permissionGroupId: string | undefined,
    ) {
      if (!permissionGroupId) {
        return;
      }
      await apiService
        .getApiInstance()
        .delete(`/permissiongroups/${permissionGroupId}`);
      permissionGroups.value = permissionGroups.value.filter(
        permissionGroup => permissionGroup.id !== permissionGroupId,
      );
    }

    async function unlinkTeamFromPermissionGroup({
      permissionGroupId,
      teamId,
    }: {
      permissionGroupId: string;
      teamId: string;
    }) {
      await apiService
        .getApiInstance()
        .delete(`/permissiongroups/${permissionGroupId}/teams/rel/${teamId}`);
      return loadPermissionGroups();
    }

    async function addTeamToPermissionGroup({
      permissionGroupId,
      teamId,
    }: {
      permissionGroupId: string;
      teamId: string;
    }) {
      await apiService
        .getApiInstance()
        .put(`/permissiongroups/${permissionGroupId}/teams/rel/${teamId}`, {});
      return loadPermissionGroups();
    }

    async function removePermissionGroupFromSpec({specId}: {specId: string}) {
      await apiService
        .getApiInstance()
        .patch(`/specs/${specId}`, {permissionGroupId: null});
      return loadPermissionGroups();
    }

    async function addSpecToPermissionGroup({
      permissionGroupId,
      specId,
    }: {
      permissionGroupId: string;
      specId: string;
    }) {
      await apiService
        .getApiInstance()
        .patch(`/specs/${specId}`, {permissionGroupId});
      return loadPermissionGroups();
    }

    function $reset() {
      roles.value = [];
      emailTemplates.value = [];
      permissionGroups.value = [];
      loggingOptions.value = {
        itemsPerPage: 10,
        page: 1,
        sortBy: [{key: 'date', order: 'desc'}],
        filter: {nameSearch: undefined},
      };
      loggingNameSearch.value = '';
      emailTemplateDetails.value = {} as EmailTemplate;
      permissionGroupDetails.value = undefined;
      logs.value = [];
      loggingCount.value = 0;
      optionsEmail.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
      newOptionsPermissionGroups.value = {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        groupBy: [],
        multiSort: false,
        mustSort: false,
        filter: {},
      };
    }

    return {
      roles,
      emailTemplates,
      permissionGroups,
      loggingOptions,
      loggingNameSearch,
      emailTemplateDetails,
      permissionGroupDetails,
      logs,
      loggingCount,
      optionsEmail,
      newOptionsPermissionGroups,
      getPermissionGroupById,
      getRoles,
      onlyGetRoles,
      saveLoggingOptions,
      saveLoggingSearch,
      getLogging,
      getLoggingCount,
      loadEmailTemplates,
      getEmailTemplateDetails,
      editEmailTemplate,
      loadPermissionGroups,
      loadPermissionGroupById,
      fetchPermissionGroupById,
      createPermissionGroup,
      editPermissionGroup,
      deletePermissionGroup,
      unlinkTeamFromPermissionGroup,
      addTeamToPermissionGroup,
      removePermissionGroupFromSpec,
      addSpecToPermissionGroup,
      $reset,
    };
  },
  {persist: true},
);
